import isEqual from 'react-fast-compare';
import _ from 'underscore';

type Arguments = Array<unknown>;

/**
 * Return a function that behaves the same as `fn`, but avoids calling `fn` if each argument is
 * equal to the corresponding argument of the last call to `fn`. "Equal" can mean either strictly
 * equal or deep equals, depending on the value of `useDeepEquality`.
 *
 * @param fn - pure function that accepts immutable arguments and returns an immutable value
 * @param useDeepEquality - if true, compare arguments via deep equality instead of strict equality
 */
export default function memoizeLastCall<A extends Arguments, R>(
  fn: (...args: A) => R,
  {useDeepEquality}: {useDeepEquality?: boolean} = {}
): (...args: A) => R {
  let lastCall: {args: A; retVal: R} | null = null;
  return (...args) => {
    if (
      lastCall == null ||
      !_.zip(lastCall.args, args).every(([oldArg, arg]) =>
        useDeepEquality ? isEqual(oldArg, arg) : oldArg === arg
      )
    ) {
      lastCall = {args, retVal: fn(...args)};
    }
    return lastCall.retVal;
  };
}
